<template>
  <div class="work-plan-container pa-4" style="background-color: #F3F4F6; min-height: 100vh;">
    <v-row>
      <!-- Main Content Left -->
      <v-col cols="12" md="9">
        <v-card class="rounded-xl pa-4 elevation-0 h-100">
          <!-- Header Actions -->
          <div class="d-flex align-center justify-space-between mb-4">
            <h2 class="text-subtitle-1 font-weight-bold text-uppercase mb-0 d-flex align-center flex-wrap">
              {{ activeTab === 0 ? 'Quản lý ca làm việc chi nhánh' : 'Quản lý tăng ca OT chi nhánh' }} -&nbsp;
              <v-menu v-model="monthMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                max-width="290px" min-width="290px">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"
                    class="teal--text text--darken-4 cursor-pointer hover-underline d-inline-flex align-center">
                    {{ selectedMonthText }}
                    <v-icon color="teal darken-4" class="ml-1">mdi-menu-down</v-icon>
                  </span>
                </template>
                <v-date-picker v-model="selectedMonth" type="month" locale="vi-vn" no-title scrollable
                  color="teal darken-4" @change="onMonthChange"></v-date-picker>
              </v-menu>
            </h2>
          </div>

          <v-tabs v-model="activeTab" background-color="transparent" color="teal darken-4" class="mb-4">
            <v-tab class="font-weight-bold">Ca làm việc</v-tab>
            <v-tab class="font-weight-bold">Tăng ca OT</v-tab>
          </v-tabs>

          <v-row class="mb-4 align-end">
            <v-col cols="12" md="3" class="py-0">
              <span class="text-caption grey--text text--darken-1 font-weight-medium">Chi nhánh:</span>
              <v-select v-model="selectedBranch" :items="branches" outlined dense hide-details
                class="rounded-lg mt-1 font-weight-medium" background-color="white"></v-select>
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <span class="text-caption grey--text text--darken-1 font-weight-medium">Thời gian:</span>
              <div class="d-flex align-center mt-1 border rounded-lg px-2 bg-white"
                style="height: 40px; border: 1px solid #E0E0E0;">
                <v-icon small :color="canPrevWeek ? 'grey darken-2' : 'grey lighten-3'"
                  :class="{ 'cursor-pointer': canPrevWeek }" :disabled="!canPrevWeek"
                  @click="canPrevWeek && prevWeek()">mdi-chevron-left</v-icon>
                <span class="mx-auto font-weight-medium text-body-2">{{ dateRangeText }}</span>
                <v-icon small :color="canNextWeek ? 'grey darken-2' : 'grey lighten-3'"
                  :class="{ 'cursor-pointer': canNextWeek }" :disabled="!canNextWeek"
                  @click="canNextWeek && nextWeek()">mdi-chevron-right</v-icon>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="py-0 d-flex justify-end mt-4 mt-md-0">
              <v-btn outlined color="teal darken-2" class="rounded-lg mr-3" height="40" width="40" min-width="40"
                @click="loadData" title="Tải lại">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn outlined color="blue darken-2" class="rounded-lg mr-3 font-weight-bold text-none" height="40"
                v-show="false" @click="openTestCheckin">
                <v-icon left small>mdi-clock-outline</v-icon> Test chấm công
              </v-btn>
              <v-btn color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text" height="40">
                <v-icon left small color="white">mdi-calendar-check</v-icon> Lưu lịch đã tạo
              </v-btn>
            </v-col>
          </v-row>

          <!-- Legend -->
          <div class="d-flex align-center mb-4 text-caption font-weight-medium">
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-green"></div> Sáng (06:00 - 14:00)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-orange"></div> Chiều (14:00 - 22:00)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-blue"></div> Gãy (tùy lịch sắp xếp)
            </div>
            <div class="d-flex align-center mr-6">
              <div class="legend-box bg-grey"></div> Nghỉ
            </div>
          </div>

          <!-- Schedule Table -->
          <div style="position: relative;">
            <v-progress-linear v-if="loading" indeterminate color="teal darken-4" absolute top
              style="z-index: 2;"></v-progress-linear>
            <v-simple-table class="schedule-table border" fixed-header height="550px"
              :style="{ border: '1px solid #E0E0E0', opacity: loading ? 0.6 : 1 }">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center font-weight-bold" style="width: 200px;">Nhân sự</th>
                    <th class="text-center font-weight-bold" v-for="day in days" :key="day.date"
                      :class="{ 'greyed-out-header': day.isGreyedOut }">
                      {{ day.name }}<br><span class="grey--text font-weight-regular"
                        :class="{ 'greyed-out-text': day.isGreyedOut }">{{ day.date }}</span>
                    </th>
                    <th class="text-center font-weight-bold" style="width: 80px;">Tổng Giờ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="emp in employees" :key="emp.id">
                    <td class="px-2 py-3">
                      <div class="d-flex align-center">
                        <v-avatar size="40" class="mr-3 avatar-clickable" @click="selectedEmployeeId = emp.id"
                          title="Chọn nhanh nhân sự">
                          <img :src="emp.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold text-body-2">{{ emp.name }}</div>
                          <div class="text-caption grey--text">{{ emp.id }}</div>
                        </div>
                      </div>
                    </td>
                    <td v-for="(shift, index) in emp.shifts" :key="index" class="text-center px-1"
                      :class="{ 'greyed-out-cell': days[index] && days[index].isGreyedOut }">
                      <template v-if="days[index] && days[index].isGreyedOut">
                        <div class="greyed-out-placeholder">—</div>
                      </template>
                      <template v-else>
                        <!-- Rotation Badge -->
                        <div v-if="days[index] && getRotationDetail(emp.id, days[index].fullDate)" class="mb-1">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip v-bind="attrs" v-on="on" color="indigo lighten-5"
                                class="indigo--text text--darken-3 font-weight-bold px-1 py-0" x-small
                                style="font-size: 9px !important; border: 1px solid #C5CAE9; height: 16px;">
                                <v-icon x-small class="mr-1" color="indigo darken-3">mdi-swap-horizontal</v-icon>
                                Điều chuyển
                              </v-chip>
                            </template>
                            <span>
                              <strong>Thông tin điều chuyển:</strong><br>
                              - Ngày: {{ formatRotationTooltipDate(getRotationDetail(emp.id, days[index].fullDate))
                              }}<br>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).fromDepartmentName || getRotationDetail(emp.id, days[index].fullDate).fromDepartment">
                                - Từ: {{ getRotationDetail(emp.id, days[index].fullDate).fromDepartmentName ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).fromDepartment }}<br>
                              </span>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).toDepartmentName || getRotationDetail(emp.id, days[index].fullDate).toDepartment">
                                - Đến: {{ getRotationDetail(emp.id, days[index].fullDate).toDepartmentName ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).toDepartment }}<br>
                              </span>
                              <span
                                v-if="getRotationDetail(emp.id, days[index].fullDate).notes || getRotationDetail(emp.id, days[index].fullDate).Notes">
                                - Ghi chú: {{ getRotationDetail(emp.id, days[index].fullDate).notes ||
                                  getRotationDetail(emp.id,
                                    days[index].fullDate).Notes }}
                              </span>
                            </span>
                          </v-tooltip>
                        </div>

                        <template v-if="shift">
                          <div :class="['shift-block', shift.type]" @click="deleteShift(emp, index)"
                            style="cursor: pointer;" title="Click để xóa">
                            <div class="font-weight-bold">{{ shift.title }}</div>
                            <div class="shift-time" v-if="shift.time1">{{ shift.time1 }}</div>
                            <div class="shift-time" v-if="shift.time2">{{ shift.time2 }}</div>
                          </div>
                        </template>
                      </template>
                    </td>
                    <td class="text-center font-weight-bold text-body-2">
                      {{ emp.totalHours }}h
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Debug JSON -->
          <div class="mt-6" v-show="false">
            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-2 d-flex align-center">
              <v-icon small class="mr-2" color="grey darken-2">mdi-code-json</v-icon>
              Dữ liệu JSON Debug (employees)
            </div>
            <pre class="pa-3 rounded-lg grey lighten-4 text-caption font-weight-medium overflow-x-auto"
              style="border: 1px solid #E0E0E0; max-height: 300px; font-family: monospace;">{{ employeesDebugJson }}</pre>
          </div>

        </v-card>
      </v-col>

      <!-- Right Panel: Registration -->
      <v-col cols="12" md="3">
        <v-card class="rounded-xl pa-4 elevation-0 h-100 d-flex flex-column">
          <h3 class="text-subtitle-1 font-weight-bold text-uppercase mb-4">{{ activeTab === 0 ? 'Đăng ký ca làm việc' :
            'Đăng ký tăng ca OT' }}</h3>

          <!-- Dynamic Employee Selector -->
          <v-select v-model="selectedEmployeeId" :items="employees" item-text="name" item-value="id" outlined dense
            hide-details class="rounded-lg mb-6 font-weight-medium" background-color="#F9FAFB"
            label="Chọn nhân viên đăng ký">
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="32" class="mr-2">
                  <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2" style="line-height: 1.2;">{{ item.name }}</div>
                  <div class="text-caption grey--text" style="line-height: 1.2;">{{ item.id }}</div>
                </div>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="32" class="mr-2">
                  <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                </v-avatar>
                <div>
                  <div class="font-weight-bold text-body-2">{{ item.name }}</div>
                  <div class="text-caption grey--text">{{ item.id }}</div>
                </div>
              </div>
            </template>
          </v-select>

          <div class="mb-4" v-if="days && days.length > 0">
            <div class="d-flex justify-space-between text-caption font-weight-medium mb-2 px-1">
              <span v-for="day in days" :key="'reg-name-' + day.date" :class="{ 'greyed-out-text': day.isGreyedOut }"
                style="width: 14%; text-align: center;">{{ day.name === 'Chủ Nhật' ? 'CN' : day.name }}</span>
            </div>
            <div class="d-flex justify-space-between px-1">
              <v-icon v-for="(day, index) in days" :key="'reg-icon-' + day.date"
                :color="day.isGreyedOut ? 'grey lighten-3' : (selectedDays.includes(index) ? 'teal darken-2' : 'grey lighten-1')"
                small style="width: 14%; text-align: center; cursor: pointer;" :disabled="day.isGreyedOut"
                @click="toggleRegDay(index)">
                {{ day.isGreyedOut ? 'mdi-minus-box-outline' : (selectedDays.includes(index) ? 'mdi-checkbox-marked' :
                  'mdi-checkbox-blank-outline') }}
              </v-icon>
            </div>
            <div class="d-flex justify-space-between text-caption grey--text px-1 mt-1"
              style="font-size: 10px !important;">
              <span v-for="day in days" :key="'reg-date-' + day.date" :class="{ 'greyed-out-text': day.isGreyedOut }"
                style="width: 14%; text-align: center;">{{ day.date }}</span>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="mb-4">
            <div class="font-weight-medium text-body-2 mb-2">{{ activeTab === 0 ? 'Chọn ca làm việc' : 'Chọn thời gian'
              }}
            </div>
            <v-radio-group v-model="selectedShift" hide-details class="mt-0 pt-0">
              <v-radio v-if="activeTab === 0" value="sang" color="teal">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium" style="color: #4CAF50;">Ca sáng (06:00 - 14:00)</span>
                </template>
              </v-radio>
              <v-radio v-if="activeTab === 0" value="chieu" color="teal" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium" style="color: #FF9800;">Ca Chiều (14:00 - 22:00)</span>
                </template>
              </v-radio>
              <v-radio value="gay" color="blue" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-bold" style="color: #2196F3;">
                    {{ activeTab === 0 ? 'Ca Gãy (Chọn thời gian)' : 'Chọn thời gian' }}
                  </span>
                </template>
              </v-radio>
              <v-radio v-if="activeTab === 0" value="nghi" color="grey" class="mt-2">
                <template v-slot:label>
                  <span class="text-body-2 font-weight-medium" style="color: #757575;">Nghỉ (OFF)</span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <div v-if="selectedShift === 'gay'" class="mb-4 pl-8">
            <div class="text-caption font-weight-medium mb-2">
              {{ activeTab === 0 ? 'Nhập thời gian ca gãy:' : 'Nhập thời gian tăng ca:' }}
            </div>
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Từ</div>
                <v-text-field v-model="splitTime1From" dense outlined hide-details
                  class="rounded-lg text-body-2 bg-white"></v-text-field>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Đến</div>
                <v-text-field v-model="splitTime1To" dense outlined hide-details
                  class="rounded-lg text-body-2 bg-white"></v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="mb-2">
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Từ</div>
                <v-text-field v-model="splitTime2From" dense outlined hide-details class="rounded-lg text-body-2"
                  :disabled="activeTab === 1" :background-color="activeTab === 1 ? '#F3F4F6' : 'white'"></v-text-field>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text mb-1">Đến</div>
                <v-text-field v-model="splitTime2To" dense outlined hide-details class="rounded-lg text-body-2"
                  :disabled="activeTab === 1" :background-color="activeTab === 1 ? '#F3F4F6' : 'white'"></v-text-field>
              </v-col>
            </v-row>
          </div>

          <div class="mb-4 mt-auto pt-4">
            <div class="text-caption font-weight-bold mb-2">Ghi chú <span class="font-weight-regular grey--text">(Không
                bắt
                buộc)</span></div>
            <v-textarea outlined dense hide-details rows="3" placeholder="Nhập ghi chú nếu có..."
              class="rounded-lg text-body-2"></v-textarea>
          </div>

          <v-row dense>
            <v-col cols="6">
              <v-btn outlined block color="grey darken-1" class="rounded-lg font-weight-bold text-none"
                @click="resetForm">Hủy</v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn block color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text"
                :loading="loading" @click="saveRegistration">Lưu</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Test Chấm Công -->
    <v-dialog v-model="testCheckinDialog" max-width="500px">
      <v-card class="rounded-xl pa-4">
        <v-card-title class="headline font-weight-bold text-h6 px-2 pb-4">
          Test chấm công
        </v-card-title>
        <v-card-text class="px-2">
          <!-- Chọn nhân viên -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Chọn nhân viên:</span>
            <v-select v-model="testCheckinEmployeeId" :items="employees" item-text="name" item-value="id" outlined dense
              hide-details class="rounded-lg mt-1 font-weight-medium" background-color="white" label="Chọn nhân viên">
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="32" class="mr-2">
                    <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-body-2" style="line-height: 1.2;">{{ item.name }}</div>
                    <div class="text-caption grey--text" style="line-height: 1.2;">{{ item.id }}</div>
                  </div>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="32" class="mr-2">
                    <img :src="item.avatar || defaultAvatar" alt="avatar" @error="onAvatarError">
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold text-body-2">{{ item.name }}</div>
                    <div class="text-caption grey--text">{{ item.id }}</div>
                  </div>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Ngày chấm công -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Ngày chấm công:</span>
            <v-menu v-model="testCheckinDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckinDateText" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-calendar"></v-text-field>
              </template>
              <v-date-picker v-model="testCheckinDate" no-title scrollable color="teal darken-4"
                @change="testCheckinDateMenu = false"></v-date-picker>
            </v-menu>
          </div>

          <!-- Giờ checkin -->
          <div class="mb-4">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Giờ checkin:</span>
            <v-menu ref="checkinTimeMenu" v-model="testCheckinTimeMenu" :close-on-content-click="false"
              :nudge-right="40" transition="scale-transition" offset-y max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckinTime" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-clock-outline"></v-text-field>
              </template>
              <v-time-picker v-if="testCheckinTimeMenu" v-model="testCheckinTime" full-width format="24hr"
                color="teal darken-4" @click:minute="$refs.checkinTimeMenu.save(testCheckinTime)"></v-time-picker>
            </v-menu>
          </div>

          <!-- Giờ checkout -->
          <div class="mb-2">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">Giờ checkout:</span>
            <v-menu ref="checkoutTimeMenu" v-model="testCheckoutTimeMenu" :close-on-content-click="false"
              :nudge-right="40" transition="scale-transition" offset-y max-width="290px" min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="testCheckoutTime" readonly outlined dense hide-details
                  class="rounded-lg mt-1 font-weight-medium" background-color="white" v-bind="attrs" v-on="on"
                  append-icon="mdi-clock-outline"></v-text-field>
              </template>
              <v-time-picker v-if="testCheckoutTimeMenu" v-model="testCheckoutTime" full-width format="24hr"
                color="teal darken-4" @click:minute="$refs.checkoutTimeMenu.save(testCheckoutTime)"></v-time-picker>
            </v-menu>
          </div>

          <!-- Kết quả chấm công -->
          <v-expand-transition>
            <div v-if="testCheckinResult" class="mt-4 pa-4 rounded-lg border-teal-light"
              style="background: linear-gradient(135deg, #E0F2F1 0%, #E8F5E9 100%); border: 1px solid #B2DFDB;">
              <div class="d-flex align-center mb-3">
                <v-icon color="teal darken-2" class="mr-2">mdi-check-circle-outline</v-icon>
                <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4">Kết quả tính công</span>
              </div>
              <v-row dense class="text-body-2">
                <v-col cols="6" class="grey--text text--darken-2 py-1">Mã nhân viên:</v-col>
                <v-col cols="6" class="font-weight-bold text-right py-1">{{ testCheckinResult.employeeCode ||
                  testCheckinResult.EmployeeCode || '—' }}</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Ngày làm việc:</v-col>
                <v-col cols="6" class="font-weight-bold text-right py-1">{{ formatResultDate(testCheckinResult.workDate
                  ||
                  testCheckinResult.WorkDate) }}</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Giờ công thực tế:</v-col>
                <v-col cols="6" class="font-weight-bold text-right green--text text--darken-2 py-1">{{
                  testCheckinResult.actualTime !== undefined ? testCheckinResult.actualTime :
                    (testCheckinResult.ActualTime
                      !== undefined ? testCheckinResult.ActualTime : 0) }} giờ</v-col>

                <v-col cols="6" class="grey--text text--darken-2 py-1">Tăng ca hợp lệ:</v-col>
                <v-col cols="6" class="font-weight-bold text-right blue--text text--darken-2 py-1">{{
                  testCheckinResult.validOvetime !== undefined ? testCheckinResult.validOvetime :
                    (testCheckinResult.ValidOvetime !== undefined ? testCheckinResult.ValidOvetime :
                      (testCheckinResult.validOvertime !== undefined ? testCheckinResult.validOvertime : 0)) }} giờ</v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card-text>
        <v-card-actions class="px-2 pt-4">
          <v-btn outlined color="red darken-1" class="rounded-lg font-weight-bold text-none" :loading="loading"
            @click="resetEnroll">
            <v-icon left small>mdi-delete-outline</v-icon> Reset ngày
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined color="grey darken-1" class="rounded-lg font-weight-bold text-none mr-2"
            @click="closeTestCheckin">Hủy</v-btn>
          <v-btn color="teal darken-4" dark class="rounded-lg font-weight-bold text-none white--text"
            @click="saveTestCheckin">Đồng ý</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import api from './api.js';

export default {
  name: 'WorkPlan',
  data() {
    return {
      loading: false,
      defaultAvatar: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y',
      selectedBranch: '',
      branches: [],
      selectedShift: 'sang',
      selectedEmployeeId: 'NS4869',
      selectedDays: [],
      splitTime1From: '08:00',
      splitTime1To: '12:00',
      splitTime2From: '',
      splitTime2To: '',
      currentWeekStart: moment().startOf('month').startOf('isoWeek'),
      selectedMonth: moment().format('YYYY-MM'),
      monthMenu: false,
      activeTab: 0,
      testCheckinDialog: false,
      testCheckinEmployeeId: '',
      testCheckinDate: moment().format('YYYY-MM-DD'),
      testCheckinDateMenu: false,
      testCheckinTime: '08:00',
      testCheckinTimeMenu: false,
      testCheckoutTime: '17:00',
      testCheckoutTimeMenu: false,
      testCheckinResult: null,
      rotations: [],
      days: [],
      employees: [
        {
          id: 'NS4869',
          name: 'Phạm Thị Trúc Ngọc',
          avatar: '',
          totalHours: 48,
          shifts: [
            { type: 'sang', title: 'CA SÁNG', time1: '06:00 - 14:00' },
            { type: 'gay', title: 'CA GÃY', time1: '08:00 - 10:00', time2: '14:00 - 20:00' },
            { type: 'chieu', title: 'CA CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
            { type: 'sang', title: 'SÁNG', time1: '06:00 - 14:00' },
            { type: 'chieu', title: 'CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
          ]
        },
        {
          id: 'NS4870',
          name: 'Trương Thế Vinh',
          avatar: '',
          totalHours: 48,
          shifts: [
            { type: 'sang', title: 'CA SÁNG', time1: '06:00 - 14:00' },
            { type: 'gay', title: 'CA GÃY', time1: '08:00 - 10:00', time2: '14:00 - 20:00' },
            { type: 'chieu', title: 'CA CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
            { type: 'sang', title: 'SÁNG', time1: '06:00 - 14:00' },
            { type: 'chieu', title: 'CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
          ]
        },
        {
          id: 'NS4871',
          name: 'Phạm Trúc Ngọc',
          avatar: '',
          totalHours: 48,
          shifts: [
            { type: 'sang', title: 'CA SÁNG', time1: '06:00 - 14:00' },
            { type: 'gay', title: 'CA GÃY', time1: '08:00 - 10:00', time2: '14:00 - 20:00' },
            { type: 'chieu', title: 'CA CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
            { type: 'sang', title: 'SÁNG', time1: '06:00 - 14:00' },
            { type: 'chieu', title: 'CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
          ]
        },
        {
          id: 'NS4872',
          name: 'Trương Văn Công',
          avatar: '',
          totalHours: 48,
          shifts: [
            { type: 'sang', title: 'CA SÁNG', time1: '06:00 - 14:00' },
            { type: 'gay', title: 'CA GÃY', time1: '08:00 - 10:00', time2: '14:00 - 20:00' },
            { type: 'chieu', title: 'CA CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
            { type: 'sang', title: 'SÁNG', time1: '06:00 - 14:00' },
            { type: 'chieu', title: 'CHIỀU', time1: '06:00 - 14:00' },
            { type: 'nghi', title: 'NGHỈ' },
          ]
        }
      ]
    }
  },
  computed: {
    headerTitle() {
      return moment(this.selectedMonth, 'YYYY-MM').format('[Tháng] MM/YYYY');
    },
    selectedMonthText() {
      return moment(this.selectedMonth, 'YYYY-MM').format('[Tháng] MM/YYYY');
    },
    dateRangeText() {
      if (this.days.length === 0) return '';
      const start = this.days[0].date;
      const end = this.days[this.days.length - 1].date;
      const midDay = moment(this.currentWeekStart).add(3, 'days');
      const year = midDay.format('YYYY');
      return `${start} - ${end}/${year}`;
    },
    canPrevWeek() {
      const prevStart = moment(this.currentWeekStart).subtract(1, 'week');
      return this.isWeekInSelectedMonth(prevStart);
    },
    canNextWeek() {
      const nextStart = moment(this.currentWeekStart).add(1, 'week');
      return this.isWeekInSelectedMonth(nextStart);
    },
    employeesDebugJson() {
      return JSON.stringify(this.employees, null, 2);
    },
    testCheckinDateText() {
      return this.testCheckinDate ? moment(this.testCheckinDate).format('DD/MM/YYYY') : '';
    }
  },
  watch: {
    selectedBranch() {
      this.loadData();
    },
    activeTab(val) {
      if (val === 1) {
        this.selectedShift = 'gay';
      } else {
        this.selectedShift = 'sang';
      }
      this.loadData();
    }
  },
  apiService: null,
  created() {
    this.apiService = new api();
    this.fetchBranches();
  },
  methods: {
    fetchBranches() {
      this.loading = true;
      this.apiService.getBranches()
        .then(res => {
          if (res.data && res.data.length > 0) {
            this.branches = res.data.map(obj => ({
              text: obj.name,
              value: obj.code
            }));
            this.selectedBranch = this.branches[0].value;
          }
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
          if (this.days.length === 0) {
            this.generateCalendar();
          }
        });
    },
    onAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },
    generateCalendar() {
      const daysList = [];
      const dayNames = ['Chủ Nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

      for (let i = 0; i < 7; i++) {
        const day = moment(this.currentWeekStart).add(i, 'days');
        daysList.push({
          name: dayNames[day.day()],
          date: day.format('DD/MM'),
          fullDate: day.format('YYYY-MM-DD'),
          isGreyedOut: !day.isSame(moment(this.selectedMonth, 'YYYY-MM'), 'month')
        });
      }
      this.days = daysList;
      this.selectedDays = [];
      this.loadData();
    },
    loadData() {
      if (!this.days || this.days.length === 0) return;

      const storeCode = this.selectedBranch ? this.selectedBranch.split(' ')[0] : '';
      const fromDate = this.days[0].fullDate;
      const toDate = this.days[this.days.length - 1].fullDate;

      this.loading = true;
      this.apiService.getEmployeesByDept(storeCode)
        .then(resEmp => {
          const empList = resEmp.data.data || resEmp.data || [];
          const mappedEmployees = empList.map(emp => ({
            id: emp.code || emp.employeeCode || emp.id || '',
            name: emp.name || emp.fullName || emp.employeeName || '',
            avatar: emp.avatar || '',
            totalHours: 0,
            shifts: Array(7).fill(null)
          }));

          const typeParam = this.activeTab === 0 ? 'shift' : 'ot';
          const apiCall = this.activeTab === 0
            ? this.apiService.getWorkPlans(storeCode, fromDate, toDate, typeParam)
            : this.apiService.getOvertimePlans(storeCode, fromDate, toDate, typeParam);

          const rotationCall = this.apiService.getEmployeeRotations(this.selectedMonth + '-01')
            .catch(err => {
              console.error('Error fetching rotations:', err);
              return { data: [] };
            });

          return Promise.all([apiCall, rotationCall])
            .then(([resPlans, resRotations]) => {
              const plansList = resPlans.data.data || resPlans.data || [];
              this.rotations = resRotations.data.data || resRotations.data || [];
              if (Array.isArray(plansList)) {
                mappedEmployees.forEach(emp => {
                  const plan = plansList.find(p => {
                    const pCode = p.employeeCode || p.EmployeeCode || '';
                    return pCode === emp.id;
                  });

                  if (plan) {
                    const rawWorkPlans = this.activeTab === 0
                      ? (plan.workPlans || plan.WorkPlans || [])
                      : (plan.overtimePlans || plan.OvertimePlans || plan.workPlans || plan.WorkPlans || []);
                    const mappedShifts = rawWorkPlans.map(wp => {
                      if (!wp) return null;
                      const type = wp.type || wp.Type || '';
                      const title = wp.title || wp.Title || '';
                      const time1 = wp.time1 || wp.Time1 || '';
                      const time2 = wp.time2 || wp.Time2 || '';
                      return { type, title, time1, time2 };
                    });

                    while (mappedShifts.length < 7) {
                      mappedShifts.push(null);
                    }

                    emp.shifts = mappedShifts;
                    emp.totalHours = plan.totalHours !== undefined ? plan.totalHours : (plan.TotalHours || 0);
                  }
                });
              }
              this.employees = mappedEmployees;
              this.employees.forEach(emp => this.calculateTotalHours(emp));
            });
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    prevWeek() {
      if (!this.canPrevWeek) return;
      this.currentWeekStart = moment(this.currentWeekStart).subtract(1, 'week');
      this.generateCalendar();
    },
    nextWeek() {
      if (!this.canNextWeek) return;
      this.currentWeekStart = moment(this.currentWeekStart).add(1, 'week');
      this.generateCalendar();
    },
    isWeekInSelectedMonth(weekStart) {
      const start = moment(weekStart);
      const end = moment(weekStart).add(6, 'days');
      const targetMonth = moment(this.selectedMonth, 'YYYY-MM');
      return start.isSame(targetMonth, 'month') || end.isSame(targetMonth, 'month');
    },
    onMonthChange(val) {
      this.monthMenu = false;
      this.currentWeekStart = moment(val, 'YYYY-MM').startOf('month').startOf('isoWeek');
      this.generateCalendar();
    },
    toggleRegDay(index) {
      if (this.days[index].isGreyedOut) return;
      const pos = this.selectedDays.indexOf(index);
      if (pos > -1) {
        this.selectedDays.splice(pos, 1);
      } else {
        this.selectedDays.push(index);
      }
    },
    calculateTotalHours(employee) {
      let total = 0;
      employee.shifts.forEach(shift => {
        if (!shift || shift.type === 'nghi') return;
        if (shift.type === 'sang' || shift.type === 'chieu') {
          total += 8;
        } else if (shift.type === 'gay') {
          total += 8;
        }
      });
      employee.totalHours = total;
    },
    deleteShift(emp, index) {
      const day = this.days[index];
      if (!day) return;

      const dateStr = day.date; // e.g. "01/07"
      const confirmMsg = `Bạn có chắc chắn muốn xóa lịch làm việc ngày ${dateStr} của nhân viên ${emp.name}?`;
      if (!confirm(confirmMsg)) return;

      const employeeCode = emp.id;
      const workDate = day.fullDate; // "YYYY-MM-DD"
      const type = this.activeTab === 0 ? 'shift' : 'ot';

      this.loading = true;
      this.apiService.deleteWorkPlan(employeeCode, workDate, type)
        .then(() => {
          this.$set(emp.shifts, index, null);
          this.calculateTotalHours(emp);
        })
        .catch(err => {
          console.error(err);
          alert('Có lỗi xảy ra khi xóa lịch làm việc.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveRegistration() {
      const employee = this.employees.find(emp => emp.id === this.selectedEmployeeId);
      if (!employee) return;

      if (!this.selectedDays || this.selectedDays.length === 0) {
        alert('Vui lòng chọn ít nhất một ngày để đăng ký ca.');
        return;
      }

      const dates = this.selectedDays
        .filter(index => this.days[index] && !this.days[index].isGreyedOut)
        .map(index => this.days[index].fullDate);

      if (dates.length === 0) {
        alert('Các ngày đã chọn không hợp lệ hoặc nằm ngoài tháng hiện tại.');
        return;
      }

      const baseDate = dates[0];
      let startHour1 = null;
      let endHour1 = null;
      let startHour2 = null;
      let endHour2 = null;

      if (this.selectedShift === 'sang') {
        startHour1 = `${baseDate}T06:00:00`;
        endHour1 = `${baseDate}T14:00:00`;
      } else if (this.selectedShift === 'chieu') {
        startHour1 = `${baseDate}T14:00:00`;
        endHour1 = `${baseDate}T22:00:00`;
      } else if (this.selectedShift === 'gay') {
        startHour1 = `${baseDate}T${this.splitTime1From}:00`;
        endHour1 = `${baseDate}T${this.splitTime1To}:00`;
        if (this.activeTab === 0) {
          startHour2 = `${baseDate}T${this.splitTime2From}:00`;
          endHour2 = `${baseDate}T${this.splitTime2To}:00`;
        } else {
          startHour2 = null;
          endHour2 = null;
        }
      }

      const payload = {
        EmployeeCode: this.selectedEmployeeId,
        Dates: dates,
        ShiftSymbol: this.selectedShift,
        StartHour1: startHour1,
        EndHour1: endHour1,
        StartHour2: startHour2,
        EndHour2: endHour2,
        Type: this.activeTab === 0 ? 'shift' : 'ot'
      };

      let shiftObj = null;
      if (this.selectedShift === 'sang') {
        shiftObj = { type: 'sang', title: 'CA SÁNG', time1: '06:00 - 14:00' };
      } else if (this.selectedShift === 'chieu') {
        shiftObj = { type: 'chieu', title: 'CA CHIỀU', time1: '14:00 - 22:00' };
      } else if (this.selectedShift === 'gay') {
        shiftObj = {
          type: this.activeTab === 0 ? 'gay' : 'ot',
          title: this.activeTab === 0 ? 'CA GÃY' : 'TĂNG CA',
          time1: `${this.splitTime1From} - ${this.splitTime1To}`,
          time2: this.activeTab === 0 && this.splitTime2From && this.splitTime2To ? `${this.splitTime2From} - ${this.splitTime2To}` : null
        };
      } else if (this.selectedShift === 'nghi') {
        shiftObj = { type: 'nghi', title: 'NGHỈ' };
      }

      this.loading = true;
      const apiCall = this.activeTab === 0
        ? this.apiService.createWorkPlan(payload)
        : this.apiService.createOvertimePlan(payload);

      apiCall
        .then(res => {
          this.selectedDays.forEach(index => {
            if (this.days[index] && this.days[index].isGreyedOut) return;
            this.$set(employee.shifts, index, shiftObj);
          });
          this.calculateTotalHours(employee);

          if (!res.data || !res.data.message) {
            // alert('Đăng ký ca làm việc thành công!');
          }
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatResultDate(val) {
      if (!val) return '';
      return moment(val).format('DD/MM/YYYY');
    },
    openTestCheckin() {
      this.testCheckinResult = null;
      this.testCheckinEmployeeId = this.selectedEmployeeId || (this.employees && this.employees.length > 0 ? this.employees[0].id : '');
      this.testCheckinDialog = true;
    },
    saveTestCheckin() {
      if (!this.testCheckinEmployeeId || !this.testCheckinDate || !this.testCheckinTime || !this.testCheckoutTime) {
        alert('Vui lòng điền đầy đủ thông tin.');
        return;
      }
      const employeeCode = this.testCheckinEmployeeId;
      const workDate = this.testCheckinDate;
      const checkin = this.testCheckinTime.length === 5 ? `${this.testCheckinTime}:00` : this.testCheckinTime;
      const checkout = this.testCheckoutTime.length === 5 ? `${this.testCheckoutTime}:00` : this.testCheckoutTime;
      const type = this.activeTab === 0 ? 'shift' : 'ot';

      this.loading = true;
      this.apiService.enroll(employeeCode, workDate, checkin, checkout, type)
        .then((res) => {
          this.testCheckinResult = res.data.data || res.data || null;
          this.loadData();
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetEnroll() {
      if (!this.testCheckinEmployeeId || !this.testCheckinDate) {
        alert('Vui lòng chọn nhân viên và ngày cần reset.');
        return;
      }

      const employee = this.employees.find(emp => emp.id === this.testCheckinEmployeeId);
      const empName = employee ? employee.name : this.testCheckinEmployeeId;
      const dateText = this.testCheckinDateText;

      if (!confirm(`Bạn có chắc chắn muốn reset công ngày ${dateText} của nhân viên ${empName}?`)) {
        return;
      }

      const employeeCode = this.testCheckinEmployeeId;
      const workdate = this.testCheckinDate;

      this.loading = true;
      this.apiService.resetEnroll(employeeCode, workdate)
        .then(() => {
          this.testCheckinResult = null;
          this.loadData();
          alert('Đã reset ngày chấm công thành công.');
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeTestCheckin() {
      this.testCheckinDialog = false;
      this.testCheckinEmployeeId = '';
      this.testCheckinDate = moment().format('YYYY-MM-DD');
      this.testCheckinTime = '08:00';
      this.testCheckoutTime = '17:00';
      this.testCheckoutTimeMenu = false;
      this.testCheckinResult = null;
    },
    resetForm() {
      this.selectedShift = 'sang';
      this.selectedDays = [];
      this.splitTime1From = '08:00';
      this.splitTime1To = '12:00';
      this.splitTime2From = '';
      this.splitTime2To = '';
    },
    getRotationDetail(empId, fullDate) {
      if (!this.rotations || !Array.isArray(this.rotations)) return null;
      return this.rotations.find(r => {
        const rCode = r.employeeCode || r.EmployeeCode || '';
        if (rCode !== empId) return false;

        const rDateRaw = r.rotationDateFrom
        if (!rDateRaw) return false;

        return moment(rDateRaw).format('YYYY-MM-DD') === fullDate;
      });
    },
    formatRotationTooltipDate(rotation) {
      if (!rotation) return '';
      const rDate = rotation.rotationDateFrom || rotation.rotationDate || rotation.RotationDate || rotation.effectiveDate || rotation.EffectiveDate || rotation.date || rotation.Date;
      return moment(rDate).format('DD/MM/YYYY');
    }
  }
}
</script>

<style scoped>
.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 6px;
}

.bg-green {
  background-color: #4CAF50;
}

.bg-orange {
  background-color: #FF9800;
}

.bg-blue {
  background-color: #2196F3;
}

.bg-grey {
  background-color: #E0E0E0;
}

.schedule-table th {
  background-color: #FAFAFA !important;
  border-bottom: 1px solid #EEEEEE !important;
  color: #333333 !important;
}

.schedule-table td {
  border-bottom: 1px solid #F5F5F5 !important;
  border-right: 1px solid #F5F5F5 !important;
}

/* Custom scrollbar for fixed height table */
.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #F3F4F6;
  border-radius: 4px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}

.schedule-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}

.shift-block {
  border-radius: 8px;
  padding: 6px 2px;
  font-size: 11px;
  line-height: 1.2;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.shift-block.sang {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.shift-block.chieu {
  background-color: #FFF3E0;
  color: #E65100;
}

.shift-block.gay,
.shift-block.ot {
  background-color: #E3F2FD;
  color: #1565C0;
}

.shift-block.nghi {
  background-color: #F5F5F5;
  color: #616161;
}

.shift-time {
  margin-top: 2px;
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline;
}

.avatar-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.avatar-clickable:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Greyed out calendar elements for days in previous month */
.greyed-out-header {
  background-color: #EEEEEE !important;
  color: #BDBDBD !important;
  opacity: 0.7;
}

.greyed-out-cell {
  background-color: #F9FAFB !important;
  color: #E0E0E0 !important;
}

.greyed-out-placeholder {
  color: #E0E0E0;
  font-weight: 500;
  font-size: 14px;
}

.greyed-out-text {
  color: #BDBDBD !important;
}
</style>
